import type { PagedResult } from './types';

/**
 * Normalizes list responses defensively. Since we can't see the backend
 * source, an endpoint might reasonably return a bare array, a `{ items, ... }`
 * page, or a `{ data: [...] }` envelope. This keeps every list-fetching hook
 * from having to guess.
 */
export function unwrapList<T>(payload: unknown): PagedResult<T> {
  if (Array.isArray(payload)) {
    return { items: payload as T[], total: payload.length, page: 1, pageSize: payload.length || 1 };
  }
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.items)) {
      return {
        items: obj.items as T[],
        total: typeof obj.total === 'number' ? obj.total : (obj.items as T[]).length,
        page: typeof obj.page === 'number' ? obj.page : 1,
        pageSize: typeof obj.pageSize === 'number' ? obj.pageSize : (obj.items as T[]).length,
      };
    }
    if (Array.isArray(obj.data)) {
      return {
        items: obj.data as T[],
        total: typeof obj.total === 'number' ? obj.total : (obj.data as T[]).length,
        page: typeof obj.page === 'number' ? obj.page : 1,
        pageSize: typeof obj.pageSize === 'number' ? obj.pageSize : (obj.data as T[]).length,
      };
    }
  }
  return { items: [], total: 0, page: 1, pageSize: 0 };
}

/** Normalizes single-item responses that might be wrapped in `{ data: T }`. */
export function unwrapItem<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    const obj = payload as Record<string, unknown>;
    if (obj.data && typeof obj.data === 'object') {
      return obj.data as T;
    }
  }
  return payload as T;
}
